import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { toast } from "../../../../../components/toastNotification";
import {
  addStateEmployeeTitle,
  editStateEmployeeTitle,
  employeeTitleSaving,
  rowForEditEmployeeTitle,
} from "../atoms/atoms";
import useAddEmployeeTitle from "../hooks/useAddEmployeeTitle";
import useEditEmployeeTitle from "../hooks/useEditEmployeeTitle";
import { IEmpTitleDetails } from "../types";
import EmployeeTitleDetails from "./EmployeeTitleDetails";

interface Props {
  isAdd: boolean;
  isEdit: boolean;
  setIsAdd;
  setIsEdit;
}

export const EmployeeTitleForm: React.FC<Props> = ({
  isAdd,
  isEdit,
  setIsAdd,
  setIsEdit,
}) => {
  const [saveModal, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(employeeTitleSaving);
  const [isAddState, setAddState] = useRecoilState(addStateEmployeeTitle);
  const setEditState = useSetRecoilState(editStateEmployeeTitle);
  const queryClient = useQueryClient();
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditEmployeeTitle);
  const [newData, setNewData] = useState<IEmpTitleDetails>();
  const [changes, setChanges] = useState<boolean>(true);

  const useFormMethods = useForm<IEmpTitleDetails>({
    defaultValues: { ...rowForEdit },
  });

  const {
    formState: { isDirty },
  } = useFormMethods;

  // Add Employee Title
  const {
    mutate: empTitleForm,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddEmployeeTitle();

  // Edit Employee Title
  const {
    mutate: editEmployeeTitle,
    isSuccess: editSuccess,
    isError: editError,
    data: editResult,
    reset: resetEditData,
  } = useEditEmployeeTitle();

  const handleSave = () => {
    setSaveModal(false);
    if (isAddState) {
      empTitleForm(newData);
    } else {
      editEmployeeTitle(newData);
    }
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setIsAdd(false);
    setIsEdit(false);
  };

  const onSubmit = (data: IEmpTitleDetails) => {
    setNewData({
      ...data,
      isActive: data.isActive ? true : false,
      usedInMexico: data.usedInMexico ? true : false,
    });
    if (isAdd) {
      setSaveModal(true);
    } else if (isEdit && isDirty) {
      setSaveModal(true);
    } else {
      setIsSaving(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };

  if (addSuccess && addResult) {
    if (addResult.data.includes("Duplicate")) {
      toast.fire("Failed", addResult.data, "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.data === "Success") {
      toast.fire("Success", "Employee Title Added Successfully.", "success");
      queryClient.invalidateQueries("GetEmployeeTitle");
      handleCancel();
      setAddState(false);
      setIsAdd(false);
    } else if (addResult.data.message) {
      if (
        addResult.data.message ===
        "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
        "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "Employee Title added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Add Employee Title! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetEmployeeTitle");
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Employee Title! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetEmployeeTitle");
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Employee Title! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetEmployeeTitle");
    setAddState(false);
  }

  //Edit Result
  if (editSuccess && editResult) {
    if (editResult && editResult.data.includes("Duplicate")) {
      toast.fire("Failed", editResult.data, "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (
      (editResult && editResult.data === "Success") ||
      editResult.data === "Success"
    ) {
      toast.fire("Success", "Employee Title Updated Successfully.", "success");
      queryClient.invalidateQueries("GetEmployeeTitle");
      handleCancel();
      setRowForEdit(undefined);
      setEditState(false);
      setIsEdit(false);
    } else if (editResult.data.message) {
      if (
        editResult.data.message ===
        "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        editResult.data.message ===
        "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "New Employee Title updated successfully, but " +
          editResult.data.message,
          "warning"
        );
        setIsSaving(false);
        handleCancel();
        setRowForEdit(undefined);
      } else {
        setIsSaving(false);
        setSaveModal(false);
        toast.fire(
          "Failed",
          "Failed to Update Employee Title! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries("GetEmployeeTitle");
      setEditState(false);
      setIsEdit(false);
    } else {
      setIsSaving(false);
      toast.fire(
        "Server Error",
        "Failed to Update Employee Title! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries("GetEmployeeTitle");
      setEditState(false);
      setIsEdit(false);
    }
    resetEditData();
  }
  if (editError) {
    setIsSaving(false);
    setEditState(false);
    setIsEdit(false);
    toast.fire(
      "Server Error",
      "Failed to Update EmployeeTitle! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries("GetEmployeeTitle");
    setEditState(false);
    setIsEdit(false);
  }

  return (
    <>
      <div className="p-1 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form
            onSubmit={useFormMethods.handleSubmit(onSubmit)}
            className="ml-3"
          >
            <div className="flex flex-row justify-between items-center text-quinary font-bold font-sans text-lg">
              <div className="font-bold pt-1 pb text-primary text-base">
                {isAdd || isAddState
                  ? "Add New Employee Title"
                  : "Edit Employee Title"}
              </div>
              <div className="flex justify-between gap-2 mr-2 pb-2 pt-3">
                <SubmitHookForm
                  type="submit"
                  disabled={changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="border-b-2 border-secondary mr-2"></div>
            <div className="text-xs text-secondary italic font-normal mt-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <EmployeeTitleDetails rowForEdit={rowForEdit} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Employee Title" : "Edit Employee Title"}
          qConfirmation={`Are you sure you want to ${isAddState ? "Add " : "Update "
            } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default EmployeeTitleForm;
